import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAdminStore } from '@/lib/admin-store';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Newsletter: React.FC = () => {
  const { t, language } = useTranslation();
  const addSubscriber = useAdminStore((s) => s.addSubscriber);
  const nlSettings = useAdminStore((s) => s.settings?.newsletterContent);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'exists' | 'error'>('idle');
  const [focused, setFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2500);
      return;
    }

    const added = addSubscriber(trimmed);
    if (added) {
      setStatus('success');
      setEmail('');
    } else {
      setStatus('exists');
    }
    setTimeout(() => setStatus('idle'), 3500);
  };

  return (
    <section className="relative overflow-hidden bg-[#fafafa] dark:bg-[#0a0a0a] transition-colors duration-700">
      {/* Top border accent */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/10 dark:via-white/10 to-transparent" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        {/* Label */}
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-[9px] sm:text-[10px] font-bold tracking-[0.4em] sm:tracking-[0.5em] text-black/20 dark:text-white/20 uppercase mb-4 block"
        >
          {nlSettings?.label?.[language]?.trim() || t.newsletter.label}
        </motion.span>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-2xl sm:text-4xl md:text-5xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none mb-4 sm:mb-5"
        >
          {nlSettings?.title?.[language]?.trim() || t.newsletter.title}
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-sm sm:text-base text-black/40 dark:text-white/40 font-light leading-relaxed max-w-lg mx-auto mb-10 sm:mb-12"
        >
          {nlSettings?.subtitle?.[language]?.trim() || t.newsletter.subtitle}
        </motion.p>

        {/* Input */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          onSubmit={handleSubmit}
          className="relative max-w-md mx-auto"
        >
          <div
            className={`relative flex items-center rounded-2xl border transition-all duration-300 ${
              focused
                ? 'border-black/20 dark:border-white/20 shadow-lg shadow-black/[0.03] dark:shadow-white/[0.03]'
                : 'border-black/[0.06] dark:border-white/[0.06]'
            } bg-white dark:bg-black/40`}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={t.newsletter.placeholder}
              className="flex-1 bg-transparent px-5 sm:px-6 py-4 sm:py-5 text-sm font-medium text-black dark:text-white placeholder:text-black/25 dark:placeholder:text-white/25 focus:outline-none"
              autoComplete="email"
            />
            <button
              type="submit"
              className="mr-2 sm:mr-2.5 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-black dark:bg-white text-white dark:text-black flex items-center justify-center hover:opacity-80 active:scale-95 transition-all shrink-0"
            >
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <Check size={16} strokeWidth={3} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="arrow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>

          {/* Status messages */}
          <AnimatePresence>
            {status !== 'idle' && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.3 }}
                className={`absolute -bottom-8 left-0 right-0 text-[10px] font-bold uppercase tracking-[0.15em] ${
                  status === 'success'
                    ? 'text-green-500'
                    : status === 'exists'
                      ? 'text-amber-500'
                      : 'text-red-400'
                }`}
              >
                {status === 'success' && t.newsletter.success}
                {status === 'exists' && t.newsletter.alreadySubscribed}
                {status === 'error' && t.newsletter.invalidEmail}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.form>
      </div>

      {/* Bottom border accent */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/10 dark:via-white/10 to-transparent" />
    </section>
  );
};

export default Newsletter;
